import { useCallback, useEffect, useRef, type FunctionComponent } from 'react';

import { DeliveryLabelType } from '@/core/tamagoshi/ProductCard/components/DeliveryLabel/DeliveryLabel';
import { useLazy } from '@/domains/core/page/hooks/useLazy';
import { isGermanyPlatform } from '@/domains/core/settings/utils';
import { generateUrl } from '@/domains/productDiscovery/algolia/components/AlgoliaProductCard/AlgoliaProductCard.utils';
import { getTaxKey } from '@/domains/productDiscovery/algolia/productProcessor';
import {
  ProductCard,
  type ProductCardProps,
} from '@/domains/productDiscovery/ProductCard/ProductCard';

import type { RhinoProduct } from '../../interfaces/RhinoProduct';

export interface RhinoProductCardProps {
  className?: string;
  b2b?: ProductCardProps['b2b'];
  merchandising?: ProductCardProps['merchandising'];
  light?: ProductCardProps['light'];
  narrow?: ProductCardProps['narrow'];
  vertical?: ProductCardProps['vertical'];
  imageLoading?: ProductCardProps['imageLoading'];
  provideImageDataSrc?: boolean;
  product: RhinoProduct;
  customTitleFormatting?: boolean;
  position: number;
  trackingListName?: string;
  onClick?: () => void;
  onDisplay?: () => void;
  displayDeliveryLabel?: boolean;
}

type RhinoTaxKey = 'amountWithVat' | 'amountWithoutVat';

export const mapAlgoliaTaxKeyToRhinoTaxKey = (
  taxKey: 'with_vat' | 'without_vat',
) => {
  const taxMap: Record<string, RhinoTaxKey> = {
    with_vat: 'amountWithVat',
    without_vat: 'amountWithoutVat',
  };
  return taxMap[taxKey];
};

export const invertedRhinoTaxMap: Record<RhinoTaxKey, RhinoTaxKey> = {
  amountWithVat: 'amountWithoutVat',
  amountWithoutVat: 'amountWithVat',
};

export const mapRhinoTaxKeyToProductCardTaxKey = (
  taxKey: 'amountWithoutVat' | 'amountWithVat',
) => {
  const taxMap: Record<RhinoTaxKey, 'withoutVAT' | 'VAT'> = {
    amountWithoutVat: 'withoutVAT',
    amountWithVat: 'VAT',
  };
  return taxMap[taxKey];
};

export const RhinoProductCard: FunctionComponent<RhinoProductCardProps> = ({
  product,
  b2b,
  position,
  trackingListName,
  customTitleFormatting,
  onClick,
  onDisplay,
  displayDeliveryLabel,
  ...otherProps
}) => {
  const taxKey = mapAlgoliaTaxKeyToRhinoTaxKey(getTaxKey());
  const invertedTaxKey = invertedRhinoTaxMap[taxKey];

  const retailPrice = product.price.retailPrice?.[taxKey] ?? undefined;
  const price = product.price.currentPrice?.[taxKey] ?? undefined;
  const secondaryPrice = b2b
    ? (product.price.currentPrice?.[invertedTaxKey] ?? undefined)
    : undefined;

  const trackProductCard = useCallback(async () => {
    if (onClick) {
      onClick();
    }
  }, [onClick]);

  const ref = useRef(null);
  const { hasBeenDisplayed } = useLazy(ref, true, {
    threshold: 0.25,
  });
  useEffect(() => {
    if (hasBeenDisplayed && onDisplay) onDisplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBeenDisplayed]);

  let deliveryLabel: DeliveryLabelType | undefined;
  if (product.isFulfillment) {
    deliveryLabel = 'freeManoExpressDelivery';
  }

  return (
    <ProductCard
      {...otherProps}
      b2b={b2b}
      pro={product.isProProduct}
      title={product.title}
      price={price as number}
      retailPrice={retailPrice}
      secondaryPrice={secondaryPrice}
      imageSrc={product.imagePath}
      brandImageSrc={product.brand.imageUrl}
      brandName={product.brand.name}
      modelsCount={product.modelsCount}
      rating={product.rating?.average}
      ratingCount={product.rating?.totalRatings}
      url={generateUrl(
        {
          url: product.productSlug,
          modelId: product.modelId,
          articleId: product.articleId,
          modelsCount: product.modelsCount,
          productId: product.productId.toString(),
        },
        false,
        true,
      )}
      customTitleFormatting={customTitleFormatting}
      onClick={trackProductCard}
      tax={b2b ? mapRhinoTaxKeyToProductCardTaxKey(taxKey) : undefined}
      couponAmount={product.coupon ? product.coupon?.toString() : undefined}
      legalMention={isGermanyPlatform()}
      ref={ref}
      displayDeliveryLabel={displayDeliveryLabel}
      delivery={deliveryLabel}
    />
  );
};

RhinoProductCard.displayName = 'RhinoProductCard';
